'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AppLogo from '@/components/ui/AppLogo';
import { BadgeCheck, Star, Shield, Building2 } from 'lucide-react';

export default function AuthContainer() {
  const [mode, setMode] = useState<'login' | 'register'>('login');

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-2/5 xl:w-1/2 bg-secondary flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        <div className="blob-green absolute inset-0 pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <div>
              <div className="font-bold text-xl text-white leading-tight">West Suffolk</div>
              <div className="text-sm text-purple-300 leading-tight">Business Directory</div>
            </div>
          </div>
          <h1 className="text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
            Grow your business<br />across West Suffolk
          </h1>
          <p className="text-purple-200 text-base leading-relaxed mb-8">
            Join over 3,800 businesses already listed in the official West Suffolk
            Council Business Directory. Get discovered by local residents and businesses.
          </p>
          <div className="space-y-4">
            {[
              { icon: Building2, text: 'Free listing — get started with no commitment' },
              { icon: BadgeCheck, text: 'Council verification builds customer trust' },
              { icon: Star, text: 'Featured listings see 4× more enquiries' },
              { icon: Shield, text: 'WCAG 2.1 accessible, GDPR compliant' },
            ]?.map((item) => (
              <div key={`brand-point-${item?.text}`} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary rounded flex items-center justify-center shrink-0 mt-0.5">
                  <item.icon size={15} className="text-white" />
                </div>
                <span className="text-purple-100 text-sm leading-relaxed">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <div className="flex gap-2 items-center">
            <div className="flex -space-x-2">
              {['SB', 'NA', 'GP', 'HC']?.map((init) => (
                <div
                  key={`avatar-${init}`}
                  className="w-8 h-8 rounded-full bg-primary border-2 border-secondary flex items-center justify-center"
                >
                  <span className="text-xs font-bold text-white">{init}</span>
                </div>
              ))}
            </div>
            <p className="text-purple-300 text-xs">
              3,847 businesses already listed
            </p>
          </div>
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 xl:p-14 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={36} />
            <div>
              <div className="font-bold text-base text-foreground">West Suffolk Business Directory</div>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-muted rounded p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-semibold rounded transition-all duration-150 ${
                mode === 'login' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 text-sm font-semibold rounded transition-all duration-150 ${
                mode === 'register' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Register Business
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToRegister={() => setMode('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}